import { useState, useEffect } from 'react';
import { FolderKanban, Briefcase, Users, Mail, TrendingUp, Clock, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import api from '../../services/api';

const StatCard = ({ title, value, icon: Icon, color, trend }) => (
  <div className="bg-white dark:bg-gray-800/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{title}</p>
        <p className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-xl ${color} shadow-lg`}>
        <Icon size={24} className="text-white" />
      </div>
    </div>
    {trend && (
      <div className={`flex items-center mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 text-sm font-medium ${trend > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
        {trend > 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
        <span>{Math.abs(trend)}% this month</span>
      </div>
    )}
  </div>
);

const QuickActionCard = ({ href, icon: Icon, title, colorClass, hoverClass }) => (
  <a href={href} className={`p-5 rounded-xl ${colorClass} backdrop-blur-sm border border-transparent hover:border-current/20 transition-all duration-300 hover:scale-105 group`}>
    <Icon className="mb-3 group-hover:scale-110 transition-transform" size={28} />
    <p className="font-semibold">{title}</p>
  </a>
);

export default function Dashboard() {
  const [stats, setStats] = useState({ projects: 0, services: 0, careers: 0, applications: 0, contacts: 0 });
  const [recentContacts, setRecentContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projects, services, careers, applications, contacts] = await Promise.all([
          api.get('/projects?limit=1'),
          api.get('/services?limit=1'),
          api.get('/careers?limit=1'),
          api.get('/applications?limit=1'),
          api.get('/contact?limit=5')
        ]);
        setStats({
          projects: projects.data.total || 0,
          services: services.data.total || 0,
          careers: careers.data.total || 0,
          applications: applications.data.total || 0,
          contacts: contacts.data.total || 0
        });
        setRecentContacts(contacts.data.contacts || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="animate-pulse space-y-6">
      <div className="h-40 bg-gradient-to-r from-cyan-200 to-blue-200 dark:from-gray-800 dark:to-gray-700 rounded-2xl"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1,2,3,4].map(i => <div key={i} className="h-28 bg-gray-200 dark:bg-gray-700 rounded-xl"></div>)}
      </div>
    </div>
  );

  const cardColors = [
    'from-blue-500 to-blue-600',
    'from-cyan-500 to-cyan-600',
    'from-purple-500 to-purple-600',
    'from-emerald-500 to-emerald-600',
    'from-orange-500 to-orange-600'
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Welcome Back</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Here's what's happening with your business</p>
      </div>

      <div className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500 p-1 rounded-2xl">
        <div className="bg-white dark:bg-gray-900 rounded-xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center p-4">
              <p className="text-4xl font-bold text-cyan-600">{stats.projects}</p>
              <p className="text-sm text-gray-500 mt-1">Projects</p>
            </div>
            <div className="text-center p-4 border-l border-gray-200 dark:border-gray-700">
              <p className="text-4xl font-bold text-blue-600">{stats.services}</p>
              <p className="text-sm text-gray-500 mt-1">Services</p>
            </div>
            <div className="text-center p-4 border-l border-gray-200 dark:border-gray-700">
              <p className="text-4xl font-bold text-purple-600">{stats.careers}</p>
              <p className="text-sm text-gray-500 mt-1">Job Openings</p>
            </div>
            <div className="text-center p-4 border-l border-gray-200 dark:border-gray-700">
              <p className="text-4xl font-bold text-emerald-600">{stats.applications}</p>
              <p className="text-sm text-gray-500 mt-1">Applications</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard title="Total Projects" value={stats.projects} icon={FolderKanban} color={`bg-gradient-to-br ${cardColors[0]}`} trend={12} />
        <StatCard title="Services" value={stats.services} icon={Briefcase} color={`bg-gradient-to-br ${cardColors[1]}`} />
        <StatCard title="Job Openings" value={stats.careers} icon={Users} color={`bg-gradient-to-br ${cardColors[2]}`} />
        <StatCard title="Applications" value={stats.applications} icon={Users} color={`bg-gradient-to-br ${cardColors[3]}`} trend={8} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-5 flex items-center gap-2">
            <span className="p-2 bg-cyan-100 dark:bg-cyan-900/50 rounded-lg"><Mail size={20} className="text-cyan-600" /></span>
            Recent Inquiries
          </h3>
          {recentContacts.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <Mail size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-gray-500">No inquiries yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentContacts.map((contact) => (
                <div key={contact._id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-md">
                    {contact.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-white truncate">{contact.name}</p>
                    <p className="text-sm text-gray-500 truncate">{contact.email}</p>
                  </div>
                  <div className={`px-3 py-1.5 text-xs font-medium rounded-full ${
                    contact.status === 'new' ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400' :
                    contact.status === 'replied' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'
                  }`}>
                    {contact.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-5 flex items-center gap-2">
            <span className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-lg"><Clock size={20} className="text-amber-600" /></span>
            Quick Actions
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <QuickActionCard href="/admin/projects" icon={FolderKanban} title="Projects" colorClass="bg-blue-50 dark:bg-blue-900/20 text-blue-600" hoverClass="hover:bg-blue-100 dark:hover:bg-blue-900/40" />
            <QuickActionCard href="/admin/services" icon={Briefcase} title="Services" colorClass="bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600" hoverClass="hover:bg-cyan-100 dark:hover:bg-cyan-900/40" />
            <QuickActionCard href="/admin/careers" icon={Users} title="Careers" colorClass="bg-purple-50 dark:bg-purple-900/20 text-purple-600" hoverClass="hover:bg-purple-100 dark:hover:bg-purple-900/40" />
            <QuickActionCard href="/admin/settings" icon={TrendingUp} title="Settings" colorClass="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300" hoverClass="hover:bg-gray-200 dark:hover:bg-gray-600" />
          </div>
        </div>
      </div>
    </div>
  );
}